import React, { useState } from 'react';

export type DonutSegment = {
    label: string;
    value: number;
    color: string;
};

type DonutChartProps = {
    title: string;
    segments: DonutSegment[];
    size?: number;
    className?: string;
};

const DonutChart = ({ title, segments, size = 160, className }: DonutChartProps) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const total = segments.reduce((sum, s) => sum + s.value, 0);
    if (total === 0) return null;

    const cx = size / 2;
    const cy = size / 2;
    const outerR = size / 2 - 4;
    const innerR = outerR * 0.6;
    const gapAngle = 0.02; // radians gap between segments

    // Build arcs
    let currentAngle = -Math.PI / 2;
    const arcs = segments
        .filter(s => s.value > 0)
        .map((seg, i, arr) => {
            const fraction = seg.value / total;
            const sweepAngle = fraction * 2 * Math.PI - (arr.length > 1 ? gapAngle : 0);
            const startAngle = currentAngle + (arr.length > 1 ? gapAngle / 2 : 0);
            const endAngle = startAngle + sweepAngle;
            currentAngle += fraction * 2 * Math.PI;

            const x1Outer = cx + outerR * Math.cos(startAngle);
            const y1Outer = cy + outerR * Math.sin(startAngle);
            const x2Outer = cx + outerR * Math.cos(endAngle);
            const y2Outer = cy + outerR * Math.sin(endAngle);
            const x1Inner = cx + innerR * Math.cos(endAngle);
            const y1Inner = cy + innerR * Math.sin(endAngle);
            const x2Inner = cx + innerR * Math.cos(startAngle);
            const y2Inner = cy + innerR * Math.sin(startAngle);
            const largeArc = sweepAngle > Math.PI ? 1 : 0;

            const d = [
                `M ${x1Outer} ${y1Outer}`,
                `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2Outer} ${y2Outer}`,
                `L ${x1Inner} ${y1Inner}`,
                `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2Inner} ${y2Inner}`,
                'Z',
            ].join(' ');

            return { ...seg, d, fraction, index: i };
        });

    const hoveredSeg = hoveredIndex !== null ? arcs[hoveredIndex] : null;

    return (
        <div className={className} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</span>
            <div style={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {arcs.map((arc, i) => (
                        <path
                            key={i}
                            d={arc.d}
                            fill={arc.color}
                            opacity={hoveredIndex !== null && hoveredIndex !== i ? 0.4 : 1}
                            style={{
                                transition: 'opacity 0.15s ease, transform 0.15s ease',
                                cursor: 'pointer',
                                transformOrigin: `${cx}px ${cy}px`,
                                transform: hoveredIndex === i ? 'scale(1.04)' : 'scale(1)',
                            }}
                            onMouseEnter={() => setHoveredIndex(i)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        />
                    ))}
                </svg>
                {/* Center label */}
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div className="text-lg font-bold" style={{ color: 'var(--text-primary)', lineHeight: 1.1 }}>
                        {hoveredSeg ? `${Math.round(hoveredSeg.fraction * 100)}%` : total}
                    </div>
                    <div className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                        {hoveredSeg ? hoveredSeg.label : 'Total'}
                    </div>
                </div>
            </div>
            {/* Legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {arcs.map((seg, i) => (
                    <div
                        key={i}
                        className="flex items-center gap-2 text-xs cursor-pointer"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={() => setHoveredIndex(i)}
                        onMouseLeave={() => setHoveredIndex(null)}
                    >
                        <span
                            className="rounded-full"
                            style={{ width: 8, height: 8, backgroundColor: seg.color, flexShrink: 0 }}
                        />
                        <span style={{ flex: 1 }}>{seg.label}</span>
                        <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                            {seg.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChart;
